import axios from 'axios'
import { useAuth } from '@/composables/useAuth'
import { useEprintFunnel } from '@/composables/format-edit/useEprintFunnel'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import type {
  EprintActions,
  EprintSession
} from '@/composables/format-edit/useEprintSession'
import { createEprintFilename } from '@/utils/format-edit/eprintFilename'
import {
  isAxiosTimeout,
  pdfResultErrorToast,
  type AxiosLikeError
} from '@/utils/format-edit/eprintPdfMessages'

export function useEprintPdf(session: EprintSession, actions: EprintActions) {
  const { user } = useAuth()
  const { deviceLib, varuna, site, formLib, message } = useHostServices()
  const funnel = useEprintFunnel()

  function pdfFormData() {
    const formData = new FormData()
    formData.append('app_html', session.previewHtml)
    formData.append('pdf_option', 'A4')
    return formData
  }

  function filename() {
    return createEprintFilename({
      formatId: session.formatid,
      ext: 'pdf',
      dateFormat: formLib.dateFormat
    })
  }

  function appendCommonPdfFields(formData: FormData) {
    formData.append('filename', filename())
    formData.append('format_id', session.formatid)
    formData.append('uuid', user.value?.uid || '')
    formData.append('print_type', session.printType)
    formData.append('device_flg', deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('app_flag', site.appFlag)
    formData.append('font_name', session.selFontStyle4DB)
    formData.append('version', '2')
    formData.append('is_mypage', session.isMypage ? 'true' : 'false')
  }

  function unlockConvenience() {
    session.isSevenElevenButtonDisabled = false
    session.paymentSelectorApi?.unlockConvenience()
  }

  function lockAllPayments() {
    session.isSevenElevenButtonDisabled = true
    session.paymentSelectorApi?.lockAll()
  }

  function showOffline() {
    message({
      message:
        '現在オフラインです。インターネットにつながってから再度お試しください',
      type: 'warning'
    })
  }

  async function eprintPDF2() {
    if (!deviceLib.isOnline) {
      showOffline()
      return
    }
    session.eprintPDFSending = true
    const pdfStartTime = Date.now()
    funnel.send('pdf_generation_start', {
      payment_method: 'postpaid',
      print_type: session.printType,
      format_id: session.formatid,
      funnel_session_id: session.funnelSessionId
    })

    const formData = pdfFormData()
    appendCommonPdfFields(formData)

    try {
      const response = await axios.post(varuna.endPoints.pdfEprint, formData, {
        timeout: 60000
      })
      if (response.data.result === 'success') {
        session.resultname = 'なし'
        session.eprintResultCode = '200'
        session.eprintContentsCode = response.data.contents_code
        session.expirationDate = response.data.expiration_date
        funnel.send('pdf_generation_complete', {
          payment_method: 'postpaid',
          duration_ms: Date.now() - pdfStartTime,
          print_type: session.printType,
          format_id: session.formatid,
          funnel_session_id: session.funnelSessionId
        })
        funnel.send('code_issued', {
          contents_code: response.data.contents_code,
          expiration_date: response.data.expiration_date,
          payment_method: 'postpaid',
          price: session.eprintPrice,
          print_type: session.printType,
          format_id: session.formatid,
          funnel_session_id: session.funnelSessionId
        })
      } else {
        message(pdfResultErrorToast(response.data.message || ''))
      }
    } catch (raw) {
      const error = raw as AxiosLikeError
      funnel.send('pdf_generation_error', {
        error_message: String(error),
        payment_method: 'postpaid',
        duration_ms: Date.now() - pdfStartTime,
        print_type: session.printType,
        format_id: session.formatid,
        funnel_session_id: session.funnelSessionId
      })
      if (session.isPrePayment && session.lastPaymentData) {
        await actions.handleEprintFailureRefund(
          '技術的な問題によりコンテンツ番号が発行できませんでした'
        )
        return
      }
      message({
        duration: 0,
        showClose: true,
        message: '送信失敗しました。しばらくしてから再度お試しください',
        type: 'error'
      })
      console.error(error)
      funnel.sendError('pdf eprint api', `send pdf eprint-api failed ${error}`)
    } finally {
      session.eprintPdfEnabled = true
      session.eprintPDFSending = false
      unlockConvenience()
    }
  }

  function handleSevenElevenClick() {
    session.isSevenElevenButtonDisabled = true
    session.eprintPdfEnabled = false
    actions.scrollToTop()
    eprintPDF2()
  }

  async function eprintAtokakin(paymentData?: unknown) {
    if (!deviceLib.isOnline) {
      showOffline()
      return
    }
    session.eprintProcessingPending = false
    session.eprintPDFSending = true
    session.isEprintProcessing = true
    const pdfStartTime = Date.now()
    funnel.send('pdf_generation_start', {
      payment_method: 'prepaid',
      print_type: session.printType,
      format_id: session.formatid,
      funnel_session_id: session.funnelSessionId
    })

    const payload = paymentData as
      { paymentIntentId?: string } | null | undefined
    const paymentIntentId =
      payload?.paymentIntentId ||
      session.lastPaymentData?.stripePaymentIntentId ||
      null

    const formData = pdfFormData()
    appendCommonPdfFields(formData)
    if (paymentIntentId) {
      formData.append('payment_intent_id', paymentIntentId)
    }

    try {
      const response = await axios.post(
        varuna.endPoints.pdfEprintAtokakin,
        formData,
        { timeout: 60000 }
      )
      session.isEprintProcessing = false
      if (response.data.result === 'success') {
        funnel.send('pdf_generation_complete', {
          payment_method: 'prepaid',
          duration_ms: Date.now() - pdfStartTime,
          print_type: session.printType,
          format_id: session.formatid,
          funnel_session_id: session.funnelSessionId
        })
        session.resultname = 'なし'
        session.eprintResultCode = '200'
        session.eprintContentsCode = response.data.contents_code
        session.expirationDate = response.data.expiration_date
        if (session.isPrePayment) session.selectedStore = 'others'
        funnel.send('code_issued', {
          contents_code: response.data.contents_code,
          expiration_date: response.data.expiration_date,
          payment_method: 'prepaid',
          price: session.eprintPrice,
          print_type: session.printType,
          format_id: session.formatid,
          funnel_session_id: session.funnelSessionId
        })
      } else if (
        response.data.result === 'already_issued' &&
        response.data.contents_code
      ) {
        session.resultname = 'なし'
        session.eprintResultCode = '200'
        session.eprintContentsCode = response.data.contents_code
        session.expirationDate = response.data.expiration_date
        if (session.isPrePayment) session.selectedStore = 'others'
        message({
          duration: 0,
          showClose: true,
          message:
            response.data.message ||
            '発行済みのコンテンツ番号を表示しています。今回のお支払いは課金されていません。',
          type: 'warning'
        })
        funnel.send('duplicate_purchase_blocked', {
          block_type: 'already_issued',
          payment_method: 'prepaid',
          print_type: session.printType,
          format_id: session.formatid,
          funnel_session_id: session.funnelSessionId
        })
      } else if (response.data.result === 'processing') {
        session.eprintProcessingPending = true
        message({
          duration: 0,
          showClose: true,
          message:
            response.data.message ||
            '決済処理が続いています。番号は数分以内にマイページとメールでお知らせします。再度購入しないでください。',
          type: 'warning'
        })
        funnel.send('duplicate_purchase_blocked', {
          block_type: 'processing',
          payment_method: 'prepaid',
          print_type: session.printType,
          format_id: session.formatid,
          funnel_session_id: session.funnelSessionId
        })
      } else if (session.isPrePayment && session.lastPaymentData) {
        await actions.handleEprintFailureRefund(
          response.data.message || 'コンビニプリント番号の発行に失敗しました'
        )
      } else {
        message(pdfResultErrorToast(response.data.message || ''))
      }
    } catch (raw) {
      const error = raw as AxiosLikeError
      session.isEprintProcessing = false
      funnel.send('pdf_generation_error', {
        error_message: String(error),
        payment_method: 'prepaid',
        duration_ms: Date.now() - pdfStartTime,
        print_type: session.printType,
        format_id: session.formatid,
        funnel_session_id: session.funnelSessionId
      })
      if (paymentIntentId && isAxiosTimeout(error)) {
        session.eprintProcessingPending = true
        message({
          duration: 0,
          showClose: true,
          message:
            '決済処理が続いています。番号は数分以内にマイページとメールでお知らせします。再度購入しないでください。',
          type: 'warning'
        })
      } else {
        message({
          duration: 0,
          showClose: true,
          message: '送信失敗しました。しばらくしてから再度お試しください',
          type: 'error'
        })
      }
      console.error(error)
      funnel.sendError('pdf eprint api', `send pdf eprint-api failed ${error}`)
    } finally {
      session.eprintPDFSending = false
      session.isCreditCardProcessing = false
      if (session.eprintProcessingPending) {
        lockAllPayments()
      } else {
        session.eprintPdfEnabled = true
        unlockConvenience()
      }
    }
  }

  return { eprintPDF2, eprintAtokakin, handleSevenElevenClick, filename }
}
